import { courseAgreementsAPI } from '@/services/courseAgreements'

const state = {
  agreements: [],
  activeAgreements: [],
  loading: false,
  error: null,
  lastFetch: null
}

const getters = {
  byId: (state) => (id) =>
    state.agreements.find(a => a.id === id),

  isCourseInAgreement: (state) => (courseName) =>
    state.activeAgreements.some(a => a.name === courseName),

  getAgreementTypeName: () => (typeId) => {
    const types = {
      1: 'Pago',
      2: 'Cortesía',
      3: 'Intercambio',
      4: 'Privilegio',
      5: 'Convenio Especial'
    }
    return types[typeId] || 'Desconocido'
  },

  activeCount: (state) =>
    state.activeAgreements.length,

  totalCount: (state) =>
    state.agreements.length
}

const mutations = {
  SET_AGREEMENTS (state, agreements) {
    state.agreements = agreements
    state.lastFetch = new Date()
  },

  SET_ACTIVE_AGREEMENTS (state, agreements) {
    state.activeAgreements = agreements
  },

  ADD_AGREEMENT (state, agreement) {
    state.agreements.push(agreement)
  },

  UPDATE_AGREEMENT (state, agreement) {
    const index = state.agreements.findIndex(a => a.id === agreement.id)
    if (index >= 0) {
      state.agreements.splice(index, 1, agreement)
    }
  },

  DELETE_AGREEMENT (state, id) {
    state.agreements = state.agreements.filter(a => a.id !== id)
  },

  SET_LOADING (state, loading) {
    state.loading = loading
  },

  SET_ERROR (state, error) {
    state.error = error
  },

  CLEAR_ERROR (state) {
    state.error = null
  }
}

const actions = {
  async fetchAll ({ commit }, params = {}) {
    commit('SET_LOADING', true)
    try {
      const response = await courseAgreementsAPI.list(params)
      const agreements = response.data.results || response.data
      commit('SET_AGREEMENTS', agreements)
      commit('CLEAR_ERROR')
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error fetching course agreements:', error)
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchActive ({ commit }) {
    try {
      const response = await courseAgreementsAPI.active()
      const agreements = response.data.agreements || response.data
      commit('SET_ACTIVE_AGREEMENTS', agreements)
      commit('CLEAR_ERROR')
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error fetching active agreements:', error)
    }
  },

  async get ({ commit }, id) {
    try {
      const response = await courseAgreementsAPI.get(id)
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error(`Error fetching agreement ${id}:`, error)
      return null
    }
  },

  async create ({ commit }, data) {
    try {
      const response = await courseAgreementsAPI.create(data)
      commit('ADD_AGREEMENT', response.data)
      commit('CLEAR_ERROR')
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error creating agreement:', error)
      throw error
    }
  },

  async update ({ commit }, { id, data }) {
    try {
      const response = await courseAgreementsAPI.update(id, data)
      commit('UPDATE_AGREEMENT', response.data)
      commit('CLEAR_ERROR')
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error(`Error updating agreement ${id}:`, error)
      throw error
    }
  },

  async delete ({ commit }, id) {
    try {
      await courseAgreementsAPI.delete(id)
      commit('DELETE_AGREEMENT', id)
      commit('CLEAR_ERROR')
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error(`Error deleting agreement ${id}:`, error)
      throw error
    }
  },

  async downloadScorecard ({ commit }, reservationId) {
    try {
      const response = await courseAgreementsAPI.downloadScorecard(reservationId)

      const url = window.URL.createObjectURL(response.data)
      const link = document.createElement('a')
      link.href = url
      link.download = `carta_salida_${reservationId}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      commit('CLEAR_ERROR')
      return true
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error downloading scorecard:', error)
      throw error
    }
  },

  clearError ({ commit }) {
    commit('CLEAR_ERROR')
  },

  reset ({ commit }) {
    commit('SET_AGREEMENTS', [])
    commit('SET_ACTIVE_AGREEMENTS', [])
    commit('CLEAR_ERROR')
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
